/**
 * Directors — scroll-triggered line reveal + card fade/slide-up.
 *
 * Mirrors the hero reveal (overflow:hidden line-masks, SplitText per visual
 * line) but fires off a ScrollTrigger when the section enters the viewport,
 * then stages a second tween over the director cards (fade + slide-up).
 *
 * Lines come from two sources:
 *   1. Manual `.line-mask > .line-inner` wrappers (e.g. wordmark).
 *   2. SplitText-generated `.directors-line` lines on `[data-directors-split]`
 *      elements (intro paragraph, label heading).
 *
 * Cards are any `[data-directors-card]` element inside the section.
 *
 * Runs on initial page load (after GSAP loads) and on Swup `page:view`.
 * Respects prefers-reduced-motion.
 */

let currentTimelines = [];
let currentSplits = [];

/**
 * Read and clear the one-shot reverse-flip flag set by
 * `transitions/flip-collapse.js > out()`. When true, this page-view was
 * entered via back-navigation from a detail page that morphed into one of
 * the director cards — so replaying the card reveal would fight the
 * still-settling FLIP morph for the destination position. Snap to the final
 * state instead. The flag is consumed on first read so subsequent forward
 * navigations to /about play the reveal normally.
 */
function consumeReverseFlipFlag() {
    try {
        const flag = sessionStorage.getItem('pembleton:reverse-flip');
        if (flag === '1') {
            sessionStorage.removeItem('pembleton:reverse-flip');
            return true;
        }
    } catch {
        // sessionStorage blocked — fall through.
    }
    return false;
}

export function initDirectorsReveal() {
    const sections = document.querySelectorAll('[data-directors-reveal]');
    if (!sections.length) return;

    cleanup();

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const SplitText = window.SplitText;
    if (!gsap || !ScrollTrigger) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const reverseFlip = consumeReverseFlipFlag();

    document.fonts.ready.then(() => {
        sections.forEach((section) => {
            // Section may have been swapped out by Swup mid-wait.
            if (!document.contains(section)) return;

            const splitTargets = Array.from(section.querySelectorAll('[data-directors-split]'));

            if (SplitText) {
                splitTargets.forEach((el) => {
                    const split = new SplitText(el, {
                        type: 'lines',
                        linesClass: 'directors-line',
                        mask: 'lines',
                    });
                    currentSplits.push(split);
                });
            }

            // Reveal split-target containers (CSS hides them until split).
            gsap.set(splitTargets, { visibility: 'visible' });

            const lines = Array.from(
                section.querySelectorAll('.line-inner, .directors-line'),
            );
            const cards = Array.from(section.querySelectorAll('[data-directors-card]'));

            if (!lines.length && !cards.length) return;

            if (reducedMotion || reverseFlip) {
                if (lines.length) gsap.set(lines, { yPercent: 0, y: 0 });
                if (cards.length) gsap.set(cards, { opacity: 1, y: 0 });
                return;
            }

            if (lines.length) gsap.set(lines, { yPercent: 100, y: '0.25em' });
            if (cards.length) gsap.set(cards, { opacity: 0, y: 30 });

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top 75%',
                    once: true,
                },
            });

            if (lines.length) {
                tl.to(lines, {
                    yPercent: 0,
                    y: 0,
                    duration: 0.85,
                    stagger: 0.09,
                    ease: 'power3.out',
                });
            }

            if (cards.length) {
                tl.to(
                    cards,
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.7,
                        stagger: 0.07,
                        ease: 'power2.out',
                    },
                    lines.length ? '-=0.45' : 0,
                );
            }

            currentTimelines.push(tl);
        });
    });
}

function cleanup() {
    currentTimelines.forEach((tl) => {
        if (tl.scrollTrigger) tl.scrollTrigger.kill();
        tl.kill();
    });
    currentTimelines = [];
    currentSplits.forEach((s) => {
        try {
            s.revert();
        } catch (e) {
            // SplitText instance may be orphaned after DOM swap — ignore.
        }
    });
    currentSplits = [];
}
